/* One checkout handoff. Requests never clear or replace the Shopify cart. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (!root.bdgCheckout) root.bdgCheckout = factory(root).mount();
})(typeof window !== 'undefined' ? window : globalThis, function (env) {
  'use strict';
  const config = env.BDGCheckoutConfig || {};
  const endpoint = 'https://api.dooki.com.br/v2/public/shopify/cart';
  const selector = 'button[name="checkout"],input[name="checkout"],a[href="/checkout"],a[href="/a/checkout"],.ymp_elem_mainBtn,[data-bdg-checkout]';
  let busy = false, redirected = false, activeButton = null, saved = [], errorBox = null;
  const doc = env.document;
  const timeoutMs = config.timeoutMs || 12000;
  const handoffTimeoutMs = config.handoffTimeoutMs || Math.max(timeoutMs, 30000);
  // Temporary containment: Yampi silently omitted a variant beyond its 100-variant import.
  // Remove IDs only after a complete item/quantity/price handoff has been verified.
  const blockedCheckoutVariants = new Set([]); // 14/09/2026: as 8 variantes alem das 100 (CARAMELO, CINZA CLARO GG/XGG) foram MOVIDAS para produtos com folga e a Yampi importou; lista vazia
  const setTimer = env.setTimeout.bind(env), clearTimer = env.clearTimeout.bind(env);

  async function jsonRequest(url, options, requestTimeoutMs = timeoutMs) {
    const controller = new env.AbortController();
    let timer;
    try {
      return await Promise.race([
        (async function () {
          const response = await env.fetch(url, Object.assign({}, options, { signal: controller.signal }));
          if (!response.ok) throw new Error('HTTP_' + response.status);
          return await response.json();
        })(),
        new Promise(function (_, reject) {
          timer = setTimer(function () { controller.abort(); reject(new Error('TIMEOUT')); }, requestTimeoutMs);
        })
      ]);
    } finally { clearTimer(timer); }
  }

  // 06/10/2026: voucher BDG no site. O botao "Usar no site" da pagina do voucher abre bdgoficial.com.br/discount/CODIGO,
  // que deixa o codigo no carrinho da Shopify; o pagamento e da Yampi, que so aplica cupom dela e aceita ?promocode=.
  // Leva so codigo no formato do voucher (PREFIXO-6 hex). Qualquer falha segue o checkout normal, sem cupom.
  const voucherPattern = /^[A-Z0-9]{2,12}-[0-9A-F]{6}$/;
  function voucherCode(cart) {
    try {
      const list = cart && Array.isArray(cart.discount_codes) ? cart.discount_codes : [];
      for (let i = 0; i < list.length; i++) {
        const code = String((list[i] && list[i].code) || '').trim().toUpperCase();
        if (voucherPattern.test(code)) return code;
      }
      const match = String((doc && doc.cookie) || '').match(/(?:^|;\s*)discount_code=([^;]+)/);
      const fromCookie = match ? decodeURIComponent(match[1]).trim().toUpperCase() : '';
      return voucherPattern.test(fromCookie) ? fromCookie : '';
    } catch (_) { return ''; }
  }

  function destination(response, promocode) {
    if (!response || response.active !== true || !response.checkout_direct_url) throw new Error('INACTIVE');
    function validated(value) {
      if (typeof value !== 'string' || !value) throw new Error('INVALID_DESTINATION');
      const url = new env.URL(value);
      const allowed = config.allowedHosts || ['bdg.pay.yampi.com.br'];
      if (url.protocol !== 'https:' || url.username || url.password || url.port || allowed.indexOf(url.hostname) < 0) throw new Error('INVALID_DESTINATION');
      return url.href;
    }
    const url = validated(response.checkout_direct_url);
    const finalUrl = validated(typeof env.ymp_getUrlWithUtms === 'function' ? env.ymp_getUrlWithUtms(url) : url);
    if (!promocode || !voucherPattern.test(promocode)) return finalUrl;
    try {
      const withCode = new env.URL(finalUrl);
      withCode.searchParams.set('promocode', promocode);
      return validated(withCode.href);
    } catch (_) { return finalUrl; }
  }

  function linkedDestination(url) {
    // location.assign does not invoke Google's configured cross-domain linker.
    // Let its existing DOM listener decorate a temporary validated link just
    // before handoff. It keeps the native consent rules and cookie format.
    let link;
    try {
      if (!doc.body || typeof env.MouseEvent !== 'function') return url;
      link = doc.createElement('a');
      link.href = url;
      link.hidden = true;
      link.setAttribute('aria-hidden', 'true');
      link.tabIndex = -1;
      doc.body.appendChild(link);
      link.dispatchEvent(new env.MouseEvent('mousedown', { bubbles: true }));
      const before = new env.URL(url);
      const after = new env.URL(link.href);
      // Accept tracking parameters only; never let a listener change checkout.
      if (after.origin !== before.origin || after.pathname !== before.pathname ||
          after.username || after.password || after.hash !== before.hash) return url;
      for (const key of new Set(before.searchParams.keys())) {
        if (key === '_gl') continue;
        if (JSON.stringify(before.searchParams.getAll(key)) !==
            JSON.stringify(after.searchParams.getAll(key))) return url;
      }
      const linker = after.searchParams.get('_gl');
      if (!linker || linker.length > 4096 || !/^[A-Za-z0-9*._~-]+$/.test(linker)) return url;
      // Copy only Google's linker value, not arbitrary parameters from listeners.
      before.searchParams.set('_gl', linker);
      return before.href;
    } catch (_) {
      // A blocked or unavailable analytics library must not block checkout.
      return url;
    } finally {
      if (link && typeof link.remove === 'function') link.remove();
    }
  }

  function notifyCheckoutReady(cart) {
    try {
      if (!Number.isFinite(cart.total_price) || cart.total_price < 0) return;
      const items = cart.items.map(function (item) {
        const id = String(item.variant_id || item.id || '');
        const cents = typeof item.final_price === 'number' ? item.final_price : item.price;
        if (!/^\d+$/.test(id) || !Number.isInteger(item.quantity) || item.quantity < 1 || !Number.isFinite(cents) || cents < 0) throw new Error('INVALID_TRACKING_ITEM');
        return { item_id: id, price: cents / 100, quantity: item.quantity };
      });
      env.dispatchEvent(new env.CustomEvent('bdg:checkout:ready', {
        detail: { value: cart.total_price / 100, currency: 'BRL', items: items }
      }));
    } catch (_) { /* Analytics must never prevent the checkout handoff. */ }
  }

  function setBusy(value) {
    if (value) {
      const controls = Array.from(doc.querySelectorAll(selector + ',.bdg-upsell__add,button[name="add"],cart-drawer quantity-input input,cart-drawer quantity-input button,cart-drawer cart-remove-button a,#main-cart-items quantity-input input,#main-cart-items quantity-input button,#main-cart-items cart-remove-button a'));
      saved = controls.map(function (element) {
        const state = { element, disabled: element.disabled, aria: element.getAttribute('aria-disabled') };
        if ('disabled' in element) element.disabled = true;
        element.setAttribute('aria-disabled', 'true');
        return state;
      });
    } else {
      saved.forEach(function (state) {
        if ('disabled' in state.element) state.element.disabled = state.disabled;
        if (state.aria === null) state.element.removeAttribute('aria-disabled');
        else state.element.setAttribute('aria-disabled', state.aria);
      });
      saved = [];
    }
    const loader = doc.querySelector('.yampi-loader');
    if (loader) { loader.style.display = value ? 'block' : 'none'; loader.setAttribute('aria-hidden', value ? 'false' : 'true'); }
    if (activeButton) value ? activeButton.setAttribute('aria-busy', 'true') : activeButton.removeAttribute('aria-busy');
  }

  function clearError() { if (errorBox) { errorBox.remove(); errorBox = null; } }
  function showError(error) {
    clearError();
    errorBox = doc.createElement('div');
    errorBox.className = 'bdg-checkout-error';
    errorBox.setAttribute('role', 'alert');
    errorBox.tabIndex = -1;
    const message = doc.createElement('p');
    message.textContent = error.message === 'VARIANT_UNAVAILABLE' ? 'A opção ' + (error.itemLabel || 'selecionada') + ' não está disponível para finalizar a compra. Seus itens foram preservados. Confira o carrinho ou fale com a loja.' : error.message === 'EMPTY_CART' ? 'Seu carrinho está vazio. Adicione uma peça para continuar.' : error.message === 'CART_PENDING' ? 'Aguarde a atualização dos itens e tente novamente.' : error.message === 'CART_UNCONFIRMED' ? 'Uma alteração no carrinho não foi confirmada. Confira o carrinho antes de continuar.' : 'Não foi possível abrir o pagamento. Seus itens continuam no carrinho. Tente novamente.';
    errorBox.appendChild(message);
    if (error.message === 'CART_UNCONFIRMED' || error.message === 'VARIANT_UNAVAILABLE') {
      const review = doc.createElement('a');
      review.className = 'button'; review.textContent = 'Conferir carrinho';
      review.href = ((env.Shopify && env.Shopify.routes && env.Shopify.routes.root) || '/') + 'cart';
      errorBox.appendChild(review);
    } else if (error.message !== 'EMPTY_CART') {
      const retry = doc.createElement('button');
      retry.type = 'button'; retry.className = 'button'; retry.textContent = 'Tentar novamente';
      retry.addEventListener('click', function () { start(activeButton); });
      errorBox.appendChild(retry);
    }
    const anchor = activeButton && activeButton.isConnected ? activeButton : doc.querySelector('cart-drawer .drawer__footer,.cart__footer,#MainContent');
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(errorBox, anchor.nextSibling);
    else doc.body.appendChild(errorBox);
    errorBox.focus();
  }

  async function start(button) {
    if (busy) return false;
    busy = true; redirected = false; activeButton = button || activeButton;
    clearError(); setBusy(true);
    try {
      if (env.bdgCartState?.pending > 0 || env.bdgCartAdds > 0 || doc.querySelector('.cart__items--disabled,product-form .loading,.bdg-upsell__add[data-busy]')) throw new Error('CART_PENDING');
      if (env.bdgCartReviewRequired || env.bdgCartState?.failed.size > 0) throw new Error('CART_UNCONFIRMED');
      const route = (env.Shopify && env.Shopify.routes && env.Shopify.routes.root) || '/';
      const cart = await jsonRequest(route + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!cart || !Array.isArray(cart.items) || !cart.items.length) throw new Error('EMPTY_CART');
      const unavailable = cart.items.find(function (item) {
        return blockedCheckoutVariants.has(String(item.variant_id || item.id || ''));
      });
      if (unavailable) {
        const unavailableError = new Error('VARIANT_UNAVAILABLE');
        unavailableError.itemLabel = [unavailable.product_title || unavailable.title, unavailable.variant_title]
          .filter(function (part) { return typeof part === 'string' && part; }).join(' - ').slice(0, 180);
        throw unavailableError;
      }
      const result = await jsonRequest(endpoint, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ shop: config.shop, shopify_internal_domain: config.shop, cart_payload: cart })
      }, handoffTimeoutMs);
      const url = destination(result, voucherCode(cart));
      redirected = true;
      notifyCheckoutReady(cart);
      env.location.assign(linkedDestination(url));
      return true;
    } catch (error) {
      redirected = false;
      showError(error);
      return false;
    } finally {
      if (!redirected) { busy = false; setBusy(false); }
    }
  }

  function stop(event) { event.preventDefault(); event.stopImmediatePropagation(); }
  function activate(event) {
    const button = event.target.closest && event.target.closest(selector);
    if (!button) {
      if (busy && event.target.closest && event.target.closest('cart-remove-button a,quantity-input button,.bdg-upsell__add,button[name="add"]')) stop(event);
      return;
    }
    stop(event);
    if (!busy) start(button);
  }
  function submit(event) {
    const button = event.submitter;
    if (button && button.matches(selector)) { stop(event); if (!busy) start(button); return; }
    const form = event.target;
    // Enter in a quantity field must not submit an uncontrolled checkout.
    if (busy && form && /\/cart(?:\/add)?\/?(?:\?|$)/.test(form.getAttribute('action') || '')) stop(event);
  }
  const api = {
    start, activate, submit, destination, linkedDestination, voucherCode,
    get busy() { return busy; },
    mount: function () {
      // Window capture also supersedes older document handlers during deployment.
      env.addEventListener('click', activate, true);
      env.addEventListener('submit', submit, true);
      env.addEventListener('keydown', function (event) {
        if (event.key === ' ' && event.target.matches && event.target.matches('a[href="/checkout"],a[href="/a/checkout"]')) activate(event);
      }, true);
      doc.querySelector('#bdg-checkout-loading-note')?.remove();
      env.addEventListener('pageshow', function () { if (redirected) { redirected = false; busy = false; setBusy(false); } });
      // Compatibility calls use the same lock and validation.
      env.yampiClick = function () { return start(); };
      env.fakeClick = function (event) { if (event) stop(event); return start(event && event.currentTarget); };
      env.getNewCheckoutURL = function () { return start(); };
      return api;
    }
  };
  return api;
});
